import {
  Choose,
  If,
  Otherwise,
  When,
} from '@/utility_components/ControlStatements'
import ListCard from 'app/ProviderSearch/ListCard'
import SearchValuePropSource from 'app/ProviderSearch/SearchValueProp/Source'
import useIsValuePropShown from 'app/ProviderSearch/SearchValueProp/useIsValuePropShown'
import {
  PRIMARY_FILTERS,
  SECONDARY_FILTERS,
  SECONDARY_OR_MULTISELECT_FILTERS,
  SLOT_LOCATION_MAP,
  VIRTUAL_SLOTS_LOAD_ERROR,
  WATERFALL_VARIANT_REVEAL_STEP,
  getDroppableSecondaryFilters,
} from 'app/ProviderSearch/constants'
import {
  nextRelaxedProvidersTotal,
  shouldContinuePastRejectedPage,
  showMoreOwnsRelaxedTotal,
  splitByBudget,
} from './waterfallBudget'
import { callRudderEventForAvailableProviderShown } from 'app/ProviderSearch/rudderEventUtils'
import {
  formatPracticesFromProviders,
  formatSupplyReserveParams,
  getCappedExtendedBookingSlotListLimit,
  getFilterValueFromParsedURL,
  getGenderValuesFromIds,
  getSelectedDayOfWeek,
  getSlotsStartEndDate,
  hasSecondaryFilters,
} from 'app/ProviderSearch/utils'
import { summarizeProfileDayOfWeekSelection } from 'constants/profileDayOfWeekFilterOptions'
import classNames from 'classnames'
import Spinner from 'common/Spinner'
import Button from 'component_library/1_Atoms/Button'
import { CANCELED_API_ERROR, PAGINATION_LIMIT } from 'constants/api'
import {
  dateFormats,
  DayOfWeek,
  INTAKE_BOOKING_WINDOW_HOURS,
  sessionStorageKeys,
  SLOTS_ROW_LIMIT_V3,
} from 'constants/common'
import { RudderEvent } from 'constants/rudderEvents'
import { TreatmentFlags, treatmentFlagValues } from 'constants/treatments'
import SlotDateContext from 'context/ProviderBooking/SlotDate'
import { mergeVirtualSlotsIntoSchedule } from 'context/ProviderBooking/ProviderSlots'
import { InsuranceProviderContext } from 'context/common/InsuranceProvider'
import { TimeZoneContext } from 'context/common/TimeZoneContext'
import { useAllFilters } from 'context/ProviderSearch/AllFiltersProvider'
import { ProviderFilterTypes } from 'dataSource/ProviderSearch'
import { GET_SLOTS, PROVIDERS } from 'dataSource/ProviderSearch/routes'
import {
  buildCountDetails,
  parseProviderDataWithSlots,
} from 'hooks/slots/helper'
import useApi from 'hooks/useApi'
import useBreakpoint from 'hooks/useBreakPoint'
import useThriveAuth from 'hooks/useThriveAuth'
import useToast from 'hooks/useToast'
import _flatten from 'lodash/flatten'
import _groupBy from 'lodash/groupBy'
import _omit from 'lodash/omit'
import _pick from 'lodash/pick'
import _pickBy from 'lodash/pickBy'
import { useRouter } from 'next/router'
import qs from 'qs'
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { CachePolicies } from 'use-http'
import {
  getCurrentZonnedTime,
  getParsedQuery,
  getRemovedTimeOfDayValues,
  getTimeZoneFormattedDate,
  hasError,
  providerHasTimeOfDaySlots,
  sortGeneratedSlots,
} from 'utils/common'
import logEvent from 'utils/logEvent'
import { getTreatment } from 'utils/treatment'
import commonStyles from '../common.module.scss'
import styles from './index.module.scss'

// New interface for bucket pagination state
interface BucketPaginationState {
  offset: number
  hasMore: boolean
  isLoading: boolean
  totalCount: number
}

const ProvidersListWaterfall: React.FC<ProvidersListWatefallProps> = ({
  scrollContainer,
  groupedFilters,
  filterLookup,
  providerList,
  selectedPractice,
  inPersonFilterSelected,
  countDetails,
  onSetIsZeroProviderState,
  noWaterfallProviderAcceptsInsurance,
  setNoWaterfallProviderAcceptsInsurance,
  onSetWaterfallPractices,
  extendedBookingEnabled = false,
  isWaterfallSimplification = false,
  initialPartialBudget = null,
  onSetHasWaterfallProviders,
  onSetDowFallbackActive,
}) => {
  const router = useRouter()
  const { query } = router
  const { state } = query
  const parsedQuery = getParsedQuery(router)
  const { isSupportOrThirdPartyUser, user } = useThriveAuth()
  const { isMobileScreen, isExtraLargeSize, isAboveXLSize } = useBreakpoint()
  const showValueProp = useIsValuePropShown()
  const { showError } = useToast()
  const { businessPartner } = user ?? {}
  const [providerListLoading, setProviderListLoading] = useState<boolean>(true)
  const [waterfallResult, setWaterfallResult] = useState<
    FilterWaterfallProvider[]
  >([])
  const [showLoadMoreButton, setShowLoadMoreButton] = useState<boolean>(true)

  // New state for tracking bucket pagination
  const [bucketPaginationState, setBucketPaginationState] = useState<
    Record<number, BucketPaginationState>
  >({})
  // Mirrors each bucket's stored offset. A rejected page advances this before
  // the follow-up fetch, because setState would still hold the previous offset
  // and that fetch would repeat the same page.
  const bucketOffsetRef = useRef<Record<number, number>>({})

  // THRIV-13273: cards fetched under the variant's cap but not yet revealed, keyed
  // by bucket. Keeping them out of `waterfallResult` means every value derived from
  // it — bucket header counts, map pins, insurance counts, analytics — describes
  // only what is on screen. A reveal drains the buffer first; if the reveal step
  // still has budget, fetchProviders fills the remainder from the API.
  const partialBufferRef = useRef<Record<number, ProviderCardDetails[]>>({})
  // Providers the variant may still reveal in the current fill. Starts as the
  // initial-view budget and is reset to the reveal step on each "show more", so a
  // fill that spans several buckets (auto-advance) stops at the right total.
  // Mount-only on purpose: SearchPage only mounts this tree after exact matches
  // load, so Split is ready before the first fill. Syncing when
  // initialPartialBudget later changes would reset a live reveal (and would
  // reset whenever providerList.length changes).
  const remainingBudgetRef = useRef<number | null>(initialPartialBudget)
  // Buffer cards for this reveal step that are not on screen yet. A short buffer
  // stays here until the follow-up request finishes, so the header count updates
  // once with the whole step. Also carries the bucket they belong to.
  const pendingBufferRevealRef = useRef<ProviderCardDetails[]>([])
  const pendingBufferBucketIndexRef = useRef<number | null>(null)
  // Mirrors the buffer so render-time controls react to it; the ref is what the
  // async fetch path reads to avoid stale closures.
  const [bufferedCount, setBufferedCount] = useState<Record<number, number>>({})

  const setBucketBuffer = (
    bucketIndex: number,
    cards: ProviderCardDetails[]
  ) => {
    partialBufferRef.current = {
      ...partialBufferRef.current,
      [bucketIndex]: cards,
    }
    setBufferedCount((prev) => ({ ...prev, [bucketIndex]: cards.length }))
  }

  /**
   * A bucket can still grow if the API has more pages or if we are sitting on
   * providers held back by the cap. `bucketPaginationState.hasMore` tracks only
   * the former, so draining the buffer cannot leave it stale.
   */
  const bucketHasMore = (bucketIndex: number): boolean =>
    !!bucketPaginationState[bucketIndex]?.hasMore ||
    (bufferedCount[bucketIndex] ?? 0) > 0

  const { request: apiRequest, abort: abortApiRequest } = useApi({
    redirectOnUnauthorized: false,
    cachePolicy: CachePolicies.CACHE_FIRST,
  })
  const previousQueryRef = useRef<string>('')
  const { timeZone } = useContext(TimeZoneContext)
  const focusProviderCardRef = useRef<HTMLDivElement>(null)
  const queryRef = useRef(parsedQuery)
  const filterIndex = useRef(0)
  const removedFilters = useRef<(string | number)[]>([])
  // Full match count for the relaxation currently in effect. Show-more on an
  // older bucket must not replace this with that bucket's smaller count.
  const relaxedTotalAvailableRef = useRef<number | undefined>(undefined)
  // Each new relaxation (not a show-more page) takes the next id. The total
  // remembers which id produced it, and each rendered bucket remembers the id
  // it was created under. A relaxation that returns only duplicates never
  // becomes a bucket, so paging the previous bucket must not adopt its count.
  const nextRelaxationIdRef = useRef(0)
  const totalRelaxationIdRef = useRef<number | undefined>(undefined)
  const bucketRelaxationIdRef = useRef<Record<number, number>>({})
  const { startSlotDate, slotListLimit } = useContext(SlotDateContext)

  // refs for rudderstack event
  const countDetailsRef = useRef<{
    slot_sum: number
    provider_count: ProviderCount[]
    next_available_slot_sum: number
  }>({ slot_sum: 0, provider_count: [], next_available_slot_sum: 0 })
  // this is same as countDetailsRef the only differnce being we are not updating it's value
  // unlike countDetailsRef
  // and is used to capture updated value of parent's countDetails
  const parentCountDetailsRef = useRef<{
    slot_sum: number
    provider_count: ProviderCount[]
    next_available_slot_sum: number
  }>({ slot_sum: 0, provider_count: [], next_available_slot_sum: 0 })

  const bucketIteration = useRef(0)
  /** Prevents the DOW empty-state fallback from being triggered more than once
   *  within a single waterfall session (reset on every router.asPath change). */
  const dayOfWeekFallbackAttempted = useRef(false)

  // Ref to track the latest request ID for cancellation handling
  const latestRequestIdRef = useRef<number>(0)
  // Which waterfall control should tag the next Available Providers Shown emit.
  // Cleared once that emit happens so a later automatic bucket does not inherit it.
  const providersShownTriggerRef = useRef<'show_more' | 'find_more' | null>(
    null
  )

  const consumeProvidersShownTrigger = (): {
    showMoreProviders: boolean
    findMorePartialMatches: boolean
  } => {
    const trigger = providersShownTriggerRef.current
    providersShownTriggerRef.current = null

    return {
      showMoreProviders: trigger === 'show_more',
      findMorePartialMatches: trigger === 'find_more',
    }
  }

  const isDesktop = isExtraLargeSize || isAboveXLSize

  const { insuranceProvider } = useContext(InsuranceProviderContext)
  // Medicaid clients must never be nudged toward self-pay, so the
  // insurance filter stays applied for the whole waterfall.
  const isMedicaidSelected = Boolean(insuranceProvider?.isMedicaid)
  const droppableSecondaryFilters = useMemo(
    () => getDroppableSecondaryFilters(insuranceProvider),
    [isMedicaidSelected]
  )

  const formattedGroupedFilters = {
    ...groupedFilters,
    ...(insuranceProvider?.id &&
      query?.insuranceId &&
      insuranceProvider.id === query.insuranceId && {
        [ProviderFilterTypes.INSURANCE_ID]: [
          {
            id: insuranceProvider.id,
            value: insuranceProvider.name,
            type: ProviderFilterTypes.INSURANCE_ID,
          },
        ],
      }),
  }

  const allGroupedFilters = useAllFilters()
  // Names come from the full filter list, so a selection the state doesn't
  // offer is still listed, e.g. under "Not filtered by"
  const filterNameSource = {
    ...formattedGroupedFilters,
    ...allGroupedFilters,
    [ProviderFilterTypes.INSURANCE_ID]:
      formattedGroupedFilters[ProviderFilterTypes.INSURANCE_ID],
  }

  const getFiltersValues = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    referenceURL: Record<string, any>,
    pickPath: string[]
  ) => {
    const filters = _pick(referenceURL, pickPath)
    const notNullFilters = _pickBy(filters, (value) => value)
    const notNullFiltersKeyValue = getFilterValueFromParsedURL(
      filterNameSource,
      notNullFilters
    )
    const notNullFiltersKeyValues = _flatten(
      Object.values(notNullFiltersKeyValue)
    )
    return notNullFiltersKeyValues
  }

  const previousSelectedSlot: SelectedSlot | null = useMemo(() => {
    if (typeof window === 'undefined') {
      return null
    }

    try {
      return JSON.parse(
        sessionStorage.getItem(sessionStorageKeys.PREVIOUS_SELECTED_SLOT)
      )
    } catch {
      return null
    }
  }, [])

  const getCurrentWaterfallProviders = () => {
    const waterfallProviders = waterfallResult.reduce((acc, item) => {
      const providerDetailsArray = item.results.map(
        (result) => result.providerDetails
      )
      return [...acc, ...providerDetailsArray]
    }, [])
    return waterfallProviders
  }

  // New function to get current bucket providers
  const getCurrentBucketProviders = (bucketIndex: number) => {
    if (!waterfallResult[bucketIndex]) {
      return []
    }

    return waterfallResult[bucketIndex].results.map(
      (result) => result.providerDetails
    )
  }

  /**
   * Whether a thin batch should roll on to the next bucket, relaxing one more
   * secondary filter.
   *
   * Control treats "fewer than 5 new providers" as thin. The variant asks the
   * budget instead, because under a cap every batch looks thin and the control
   * heuristic would relax a filter on every single reveal.
   *
   * Both require a secondary filter to still be removable — otherwise there is no
   * next bucket to build and loadMoreProviders() would fall into the zero-provider
   * branch and wrongly flip isZeroProviderState while cards are on screen.
   */
  const shouldAdvanceToNextBucket = (renderedCount: number): boolean => {
    if (
      renderedCount === 0 ||
      !hasSecondaryFilters(queryRef.current, droppableSecondaryFilters)
    ) {
      return false
    }

    return isWaterfallSimplification
      ? (remainingBudgetRef.current ?? 0) > 0
      : renderedCount < 5
  }

  /**
   * Fetches providers with the selected filters.
   * And based on if filters is applied or not, either fetchPractices or fetchRestProvidersForPractice
   * Will be called to get the practices.
   * @param parsedQuery - The query parameters for the API call
   * @param bucketIndex - Optional bucket index for pagination within buckets
   * @param isSecondaryBucket - Whether this is a secondary bucket API call vs initial
   */

  const fetchProviders = async (
    parsedQuery,
    bucketIndex?: number,
    isSecondaryBucket = false
  ): Promise<void> => {
    // Cancel any pending request to prevent race conditions
    abortApiRequest()

    // Generate unique ID for this request to track if it's still the latest
    const currentRequestId = ++latestRequestIdRef.current
    // A new bucket fetch is a new relaxation, even when every provider on the
    // page was already shown and no bucket is created. Show-more keeps the id
    // stored on its bucket.
    const requestRelaxationId = isSecondaryBucket
      ? bucketIndex === undefined
        ? undefined
        : bucketRelaxationIdRef.current[bucketIndex]
      : ++nextRelaxationIdRef.current

    // `null` outside the variant, which keeps every downstream slice unbounded.
    const currentBudget = isWaterfallSimplification
      ? remainingBudgetRef.current ?? 0
      : null

    try {
      // Handle bucket pagination loading state
      if (isSecondaryBucket && bucketIndex !== undefined) {
        setBucketPaginationState((prev) => ({
          ...prev,
          [bucketIndex]: {
            ...prev[bucketIndex],
            isLoading: true,
          },
        }))
      } else {
        // Always re-assert loading. A follow-up request runs in the closure
        // from the render where this flag was already true, so a
        // `!providerListLoading` check skips the update and the bucket that
        // just finished turns it off for the rest of this request.
        setProviderListLoading(true)
      }

      const { partnershipCode } = parsedQuery

      const normalizedDayOfWeek: DayOfWeek[] | undefined = parsedQuery.dayOfWeek
        ? (Array.isArray(parsedQuery.dayOfWeek)
            ? parsedQuery.dayOfWeek
            : [parsedQuery.dayOfWeek]
          ).filter((value): value is DayOfWeek =>
            (Object.values(DayOfWeek) as string[]).includes(value)
          )
        : undefined
      const dayOfWeekForRequest = normalizedDayOfWeek?.length
        ? normalizedDayOfWeek
        : undefined

      const cappedSlotListLimit = extendedBookingEnabled
        ? getCappedExtendedBookingSlotListLimit(
            startSlotDate,
            slotListLimit,
            timeZone
          )
        : slotListLimit

      const { startDate, endDate } = getSlotsStartEndDate(
        startSlotDate,
        cappedSlotListLimit,
        timeZone,
        dayOfWeekForRequest,
        undefined,
        extendedBookingEnabled
      )

      // Extended booking anchors supply to now so providers with only far-out
      // (virtual) availability still surface as the date window is paginated.
      const supplyAnchor = extendedBookingEnabled
        ? getCurrentZonnedTime(timeZone)
        : startSlotDate
      const {
        startDate: supplyStartDate,
        endDate: supplyEndDate,
      } = getSlotsStartEndDate(
        supplyAnchor,
        slotListLimit,
        timeZone,
        dayOfWeekForRequest,
        undefined,
        extendedBookingEnabled
      )

      const isSupplyReserveFlagEnabled =
        getTreatment(TreatmentFlags.SUPPLY_RESERVE, {
          state,
        }) === treatmentFlagValues.ON
      const {
        id: b2bPartnerId,
        isActive: isB2bPartnerActive,
        partnershipCode: existingB2bPartnerKeyword,
      } = businessPartner ?? {}
      const isExistingB2bUser = isB2bPartnerActive && !!b2bPartnerId
      const b2bPartnershipCode = isExistingB2bUser
        ? existingB2bPartnerKeyword
        : partnershipCode

      // Handle offset for bucket pagination
      let offset = '0'
      const limit = PAGINATION_LIMIT.toString()

      if (isSecondaryBucket && bucketIndex !== undefined) {
        // For bucket pagination, calculate offset from internal state.
        // The ref wins so a page skipped in this same turn is not requested again.
        const currentOffset =
          bucketOffsetRef.current[bucketIndex] ??
          bucketPaginationState[bucketIndex]?.offset ??
          0
        offset = (currentOffset + PAGINATION_LIMIT).toString()
      } else if (!isSecondaryBucket) {
        // For initial bucket creation, check if parsedQuery has offset (for non-bucket calls)
        offset = parsedQuery.offset || '0'
      }

      const queryObj = {
        ..._pick(parsedQuery, [
          'state',
          'longitude',
          'latitude',
          'areaOfConcern',
          'insuranceId',
          'counselingType',
          'availabilityType',
          'orderBy',
          'gender',
          'clientAgeGroup',
          'language',
          'counselingApproach',
          'practiceId',
          'timeOfDay',
        ]),
        ...(dayOfWeekForRequest && { dayOfWeek: dayOfWeekForRequest }),
        // Always set offset and limit internally
        offset,
        limit,
        slotStartDate: supplyStartDate.toISOString(),
        slotEndDate: supplyEndDate.toISOString(),
        ...(isSupplyReserveFlagEnabled &&
          !!b2bPartnershipCode && { partnershipCode: b2bPartnershipCode }),
        ...((!!parsedQuery.timeOfDay || !!dayOfWeekForRequest) && {
          timezone: timeZone,
        }),
      }

      const qsQuery = qs.stringify(queryObj, {
        addQueryPrefix: true,
        skipNulls: true,
        indices: false,
      })
      const requestUrl = `${PROVIDERS}${qsQuery}`

      const responseProviderList = await apiRequest.get(requestUrl)

      // Check if this request is stale (a newer request was made)
      if (currentRequestId !== latestRequestIdRef.current) {
        return
      }

      if (responseProviderList === undefined) {
        throw new Error(CANCELED_API_ERROR)
      }

      const {
        result: providerListResult,
        intakeSupplyReserveDetails,
        pagination: providerPagination,
      } = responseProviderList
      hasError(apiRequest, responseProviderList)

      // A new relaxation owns the total. Show-more owns it only when this
      // bucket is the relaxation that produced the current total. The latest
      // rendered bucket can be older than that relaxation.
      const isCurrentRelaxation = isSecondaryBucket
        ? showMoreOwnsRelaxedTotal(
            requestRelaxationId,
            totalRelaxationIdRef.current
          )
        : true

      relaxedTotalAvailableRef.current = nextRelaxedProvidersTotal(
        relaxedTotalAvailableRef.current,
        providerPagination?.count,
        isCurrentRelaxation
      )

      if (isCurrentRelaxation && (providerPagination?.count ?? 0) > 0) {
        totalRelaxationIdRef.current = requestRelaxationId
      }

      // Later pages can include a provider with no practice. Mapping those throws
      // ("Cannot destructure property 'providerSlugs'") and drops the whole page,
      // so Show more stays stuck on the same request. Skip them and render the rest.
      const usableProviders = (providerListResult ?? []).filter((provider) =>
        Array.isArray(provider?.practice?.providerSlugs)
      )

      usableProviders.forEach((provider) => {
        const { providerSlugs } = provider.practice
        provider.practice.providerSlugs = providerSlugs.map((slug) => ({
          name: slug,
        }))
        provider.filters = provider.availabilityTypeIds?.reduce(
          (filters, id) => [...filters, filterLookup[id]?.[0]],
          []
        )
      })

      // Handle provider deduplication differently for bucket pagination
      let existingProviders = []
      let existingProvidersIds = []

      if (isSecondaryBucket && bucketIndex !== undefined) {
        // For bucket pagination, include all providers plus current bucket providers
        const currentBucketProviders = getCurrentBucketProviders(bucketIndex)
        existingProviders = [
          ...providerList,
          ...getCurrentWaterfallProviders(),
          ...currentBucketProviders,
        ]
      } else {
        // For initial bucket creation, only include main list and waterfall providers
        const currentWaterfallProviders = getCurrentWaterfallProviders()
        existingProviders = [...providerList, ...currentWaterfallProviders]
      }

      existingProvidersIds = existingProviders.map((item) => item.id)

      const recentNewProviders = usableProviders.filter(
        (item) => !existingProvidersIds.includes(item.id)
      )

      const supplyReserveParams = isSupplyReserveFlagEnabled
        ? formatSupplyReserveParams(intakeSupplyReserveDetails, user?.userType)
        : {}

      bucketIteration.current = bucketIteration.current + 1

      // if there are no new providers returned by the api, recall it

      if (recentNewProviders.length === 0) {
        // Handle bucket pagination with no new providers
        if (isSecondaryBucket && bucketIndex !== undefined) {
          const fetchedOffset = Number(offset)
          const returnedCount = providerListResult?.length ?? 0

          // A page of providers with no practice is not exhaustion. Step past
          // it while the API count still extends beyond this page. Duplicates
          // and a genuinely empty page still stop the bucket.
          if (
            shouldContinuePastRejectedPage(
              returnedCount,
              usableProviders.length,
              providerPagination?.count ?? 0,
              fetchedOffset
            )
          ) {
            bucketOffsetRef.current[bucketIndex] = fetchedOffset
            setBucketPaginationState((prev) => ({
              ...prev,
              [bucketIndex]: {
                ...prev[bucketIndex],
                offset: fetchedOffset,
                hasMore: true,
                isLoading: true,
              },
            }))
            fetchProviders(parsedQuery, bucketIndex, true)
            return
          }

          // Update pagination state to indicate no more providers
          setBucketPaginationState((prev) => ({
            ...prev,
            [bucketIndex]: {
              ...prev[bucketIndex],
              hasMore: false,
              isLoading: false,
            },
          }))
          emitPendingBufferRevealIfNeeded()
          // A pending buffer reveal already consumed the trigger. If there was
          // nothing to emit, this click produced no shown event and must not
          // tag a later load.
          providersShownTriggerRef.current = null
          return
        }

        // Snapshot before loadMoreProviders. That call drops the next filter
        // synchronously, and this emit still belongs to the response in hand.
        const relaxedFilters = [...removedFilters.current]
        const totalProvidersAvailable =
          relaxedTotalAvailableRef.current ?? existingProviders.length
        const startedNextBucket = loadMoreProviders()
        const willEmitFirstBucketShown =
          bucketIteration.current === 1 &&
          !hasSecondaryFilters(parsedQuery, SECONDARY_FILTERS)

        // A follow-up bucket or the first-bucket shown event still belongs to
        // this click. If neither will emit, drop the trigger.
        if (!startedNextBucket && !willEmitFirstBucketShown) {
          providersShownTriggerRef.current = null
        }

        if (!isDesktop) {
          countDetailsRef.current.slot_sum =
            countDetailsRef.current.next_available_slot_sum
        }

        // call the rudder event if it's the first bucket case
        // adding timeout because countDetailsRef is not updated at this point.
        // A follow-up fetch (next bucket, or a day-of-week fallback once that
        // path exists) owns this click. Leave the trigger on the ref for that
        // fetch. When nothing else will emit, snapshot now so a newer click
        // during the delay cannot land on this event.
        if (willEmitFirstBucketShown) {
          const shownTrigger = startedNextBucket
            ? {
                showMoreProviders: false,
                findMorePartialMatches: false,
              }
            : consumeProvidersShownTrigger()

          setTimeout(() => {
            callRudderEventForAvailableProviderShown({
              providers: existingProviders,
              countDetails: countDetailsRef.current,
              totalProvidersAvailable,
              groupedFilters: formattedGroupedFilters,
              query,
              startSlotDate,
              selectedPractice,
              user,
              intakeSupplyReserveParams: supplyReserveParams,

              directProvidersCount: providerList.length,
              indirectProvidersCount: 0,
              directSlotsCountDetails: countDetailsRef.current,
              indirectSlotsCountDetails: { slot_sum: 0, provider_count: [] },
              timeZone,
              relaxedFilters,
              ...shownTrigger,
            })
          }, 500)
        }
      } else {
        const slots = await apiRequest.post(GET_SLOTS, {
          start: startDate,
          end: endDate,
          for: recentNewProviders.map((provider) => ({
            providerId: provider.id,
            practiceId: provider?.practice?.id,
          })),
          ...(parsedQuery.timeOfDay && {
            timeOfDay: parsedQuery.timeOfDay,
            timezone: timeZone,
          }),
          ...(dayOfWeekForRequest && {
            dayOfWeek: dayOfWeekForRequest,
            timezone: timeZone,
          }),
        })

        // A newer click aborts this hook, but use-http can still resolve the
        // call after the body is parsed. Leave that click's trigger on the ref.
        if (currentRequestId !== latestRequestIdRef.current) {
          return
        }

        const generatedProviderSlots = {}
        const nextAvailabilityMap: NextAvailabilityMap = {}

        const groupedSlots = _groupBy(slots, (slot) => slot.providerId)
        recentNewProviders?.map((provider) => {
          const providerSlots = groupedSlots[provider.id]

          if (providerSlots?.length) {
            const groupedDateSlots: DateSlot = _groupBy(
              providerSlots[0]?.slots,
              (slot) =>
                getTimeZoneFormattedDate(
                  slot.start,
                  dateFormats.YEAR_MONTH_DAY_FORMAT,
                  timeZone
                )
            )
            const sortedGroupedDateSlots = sortGeneratedSlots(groupedDateSlots)
            generatedProviderSlots[provider.id] = sortedGroupedDateSlots
            //creating next availability map for providers
            nextAvailabilityMap[provider.id] =
              providerSlots?.[0]?.nextAvailableSlot ?? null
          }
        })

        if (extendedBookingEnabled) {
          // A virtual-slots failure should not blank the bucket; keep the
          // traditional results and skip the virtual merge.
          let virtualSlotsFetched = true

          try {
            virtualSlotsFetched = await mergeVirtualSlotsIntoSchedule(
              generatedProviderSlots,
              {
                providerList: recentNewProviders,
                startDate,
                endDate,
                timeZone,
                request: apiRequest,
                timeOfDay: parsedQuery.timeOfDay,
                dayOfWeek: dayOfWeekForRequest,
              }
            )
          } catch {
            virtualSlotsFetched = false
          }

          if (currentRequestId !== latestRequestIdRef.current) {
            return
          }

          if (!virtualSlotsFetched) {
            showError(VIRTUAL_SLOTS_LOAD_ERROR)
          }
        }

        // -----------------------------------------------------
        // Get removed TOD values for slot highlighting in waterfall buckets
        const removedTimeOfDayValues = getRemovedTimeOfDayValues(
          removedFilters.current
        )
        const hasRemovedTodFilters = removedTimeOfDayValues.length > 0
        const bookingWindowHours = INTAKE_BOOKING_WINDOW_HOURS

        const {
          providerCardDetails: parsedCardDetails,
        } = parseProviderDataWithSlots({
          providerSlots: generatedProviderSlots,
          providersList: recentNewProviders,
          nextAvailabilityForProviders: nextAvailabilityMap,
          startSlotDate,
          slotListLimit,
          counselingTypedFilters:
            formattedGroupedFilters[ProviderFilterTypes.COUNSELING_TYPE],
          slotsRowLimit: SLOTS_ROW_LIMIT_V3,
          error: null,
          areSlotsLoading: false,
          timeOfDay: hasRemovedTodFilters
            ? removedTimeOfDayValues
            : parsedQuery.timeOfDay,
          dayOfWeek: dayOfWeekForRequest,
          ...((hasRemovedTodFilters ||
            parsedQuery.timeOfDay ||
            dayOfWeekForRequest) && { timeZone }),
          highlightOnlyTimeOfDay: hasRemovedTodFilters,
          bookingWindowHours,
          extendedBookingEnabled,
        })

        // THRIV-13273: hold back everything past the remaining budget. Control
        // passes a null budget, so it renders the whole batch as before.
        const {
          rendered: providerCardDetails,
          buffered: overflowCardDetails,
        } = splitByBudget(parsedCardDetails, currentBudget)

        if (currentBudget !== null) {
          remainingBudgetRef.current = Math.max(
            0,
            currentBudget - providerCardDetails.length
          )
        }

        // Cards held back from a short buffer. Their slot counts were already
        // folded into countDetailsRef when they were taken off the buffer.
        const heldCards =
          isSecondaryBucket &&
          bucketIndex !== undefined &&
          pendingBufferBucketIndexRef.current === bucketIndex
            ? pendingBufferRevealRef.current
            : []

        if (heldCards.length) {
          pendingBufferRevealRef.current = []
          pendingBufferBucketIndexRef.current = null
        }

        const renderedNewProviders = providerCardDetails.map(
          (card) => card.providerDetails
        )
        const totalProviders = [
          ...existingProviders,
          ...heldCards.map((card) => card.providerDetails),
          ...renderedNewProviders,
        ]
        const recentCountDetails = buildCountDetails(providerCardDetails)

        if (isSecondaryBucket && bucketIndex !== undefined) {
          setBucketBuffer(bucketIndex, [
            ...(partialBufferRef.current[bucketIndex] ?? []),
            ...overflowCardDetails,
          ])

          // Handle bucket pagination - add providers to existing bucket.
          // Held cards join this update so the header count moves once.
          setWaterfallResult((prev) => {
            const updated = [...prev]
            updated[bucketIndex] = {
              ...updated[bucketIndex],
              results: [
                ...updated[bucketIndex].results,
                ...heldCards,
                ...providerCardDetails,
              ],
            }
            return updated
          })

          // Update pagination state for this bucket. Store the offset this
          // request actually used so a skipped page is not requested again.
          const newOffset = Number(offset)
          bucketOffsetRef.current[bucketIndex] = newOffset

          // Calculate total providers rendered across all sources
          const totalDirectProviders = providerList.length
          const totalWaterfallProviders = waterfallResult.reduce(
            (acc, bucket, idx) => {
              if (idx === bucketIndex) {
                // For the current bucket being updated, include existing + new providers
                return (
                  acc +
                  bucket.results.length +
                  heldCards.length +
                  providerCardDetails.length
                )
              }

              return acc + bucket.results.length
            },
            0
          )
          const totalProvidersRendered =
            totalDirectProviders + totalWaterfallProviders
          const hasMoreProviders =
            (providerPagination?.count || 0) > totalProvidersRendered

          setBucketPaginationState((prev) => ({
            ...prev,
            [bucketIndex]: {
              offset: newOffset,
              hasMore: hasMoreProviders,
              isLoading: false,
              totalCount: providerPagination?.count || 0,
            },
          }))

          if (shouldAdvanceToNextBucket(providerCardDetails.length)) {
            setTimeout(() => {
              loadMoreProviders()
            }, 100)
          }
        } else {
          // Handle initial bucket creation
          const waterfallData = [
            {
              filters: {
                appliedFilters: getFiltersValues(queryRef.current, [
                  ...PRIMARY_FILTERS,
                  ...SECONDARY_FILTERS,
                ]),
                removedFilters: removedFilters.current,
              },
              results: [...providerCardDetails],
              providerPagination,
              // Store the exact query used for this bucket for pagination
              bucketQuery: { ...parsedQuery },
            },
          ]

          setWaterfallResult((prevData) => [...prevData, ...waterfallData])

          // Initialize pagination state for this bucket
          const currentBucketIndex = waterfallResult.length

          if (requestRelaxationId !== undefined) {
            bucketRelaxationIdRef.current = {
              ...bucketRelaxationIdRef.current,
              [currentBucketIndex]: requestRelaxationId,
            }
          }

          // Calculate total providers rendered across all sources including the new bucket
          const totalDirectProviders = providerList.length
          const totalExistingWaterfallProviders = waterfallResult.reduce(
            (acc, bucket) => acc + bucket.results.length,
            0
          )
          const totalProvidersRendered =
            totalDirectProviders +
            totalExistingWaterfallProviders +
            providerCardDetails.length
          const hasMoreProviders =
            (providerPagination?.count || 0) > totalProvidersRendered

          setBucketBuffer(currentBucketIndex, overflowCardDetails)

          bucketOffsetRef.current[currentBucketIndex] = 0
          setBucketPaginationState((prev) => ({
            ...prev,
            [currentBucketIndex]: {
              offset: 0,
              hasMore: hasMoreProviders,
              isLoading: false,
              totalCount: providerPagination?.count || 0,
            },
          }))
        }

        // changes for rudderstack event
        countDetailsRef.current = {
          ...countDetailsRef.current,
          slot_sum:
            countDetailsRef.current?.slot_sum + recentCountDetails.slot_sum,
          provider_count: [
            ...countDetailsRef.current?.provider_count,
            ...recentCountDetails.provider_count,
          ],
          next_available_slot_sum:
            countDetailsRef.current?.next_available_slot_sum +
            recentCountDetails.next_available_slot_sum,
        }

        const parentCountDetailsProviderId = parentCountDetailsRef.current.provider_count.map(
          (provider) => Object.keys(provider)[0]
        )

        /**
         * Here we can't use countDetails instead of parentCountDetailsRef because
         * countDetails at this point is not updated on this scope
         */
        const indirectSlotsCountDetails = {
          slot_sum:
            countDetailsRef.current.slot_sum -
            parentCountDetailsRef.current.slot_sum,
          provider_count: countDetailsRef.current.provider_count.filter(
            (provider) =>
              !parentCountDetailsProviderId.includes(Object.keys(provider)[0])
          ),
          next_available_slot_sum:
            countDetailsRef.current.next_available_slot_sum -
            parentCountDetailsRef.current.next_available_slot_sum,
        }

        if (!isDesktop) {
          countDetailsRef.current.slot_sum =
            countDetailsRef.current.next_available_slot_sum
          indirectSlotsCountDetails.slot_sum =
            indirectSlotsCountDetails.next_available_slot_sum
          parentCountDetailsRef.current.slot_sum =
            parentCountDetailsRef.current.next_available_slot_sum
        }

        const shownTrigger = consumeProvidersShownTrigger()

        // A short buffer is already in this payload. If one is still pending
        // (the request that was meant to draw it did not), put it on screen now.
        emitPendingBufferRevealIfNeeded()
        callRudderEventForAvailableProviderShown({
          providers: totalProviders,
          countDetails: countDetailsRef.current,
          totalProvidersAvailable:
            relaxedTotalAvailableRef.current ?? totalProviders.length,
          groupedFilters: formattedGroupedFilters,
          query,
          startSlotDate,
          selectedPractice,
          user,
          intakeSupplyReserveParams: supplyReserveParams,

          directProvidersCount: providerList.length,
          indirectProvidersCount: totalProviders.length - providerList.length,
          directSlotsCountDetails: parentCountDetailsRef.current,
          indirectSlotsCountDetails: indirectSlotsCountDetails,
          timeZone,
          relaxedFilters: [...removedFilters.current],
          ...shownTrigger,
        })

        if (!isSecondaryBucket) {
          setProviderListLoading(false)
        }
      }
    } catch (error) {
      // Only handle error if this is still the latest request.
      // A superseded request must not clear the trigger the newer fetch still owns.
      if (currentRequestId !== latestRequestIdRef.current) {
        return // Discard error from stale request
      }

      // Handle error states for bucket pagination
      if (isSecondaryBucket && bucketIndex !== undefined) {
        setBucketPaginationState((prev) => ({
          ...prev,
          [bucketIndex]: {
            ...prev[bucketIndex],
            isLoading: false,
          },
        }))
      }

      if (error.message !== CANCELED_API_ERROR) {
        // Emit before clearing so a buffer reveal still carries this click's flag.
        emitPendingBufferRevealIfNeeded()
        showError(error?.message || 'Something went wrong')

        // A genuine (non-cancellation) failure of the main waterfall or DOW
        // fallback request is terminal for this request — nothing else will
        // clear the spinner, so stop it here rather than leaving it stuck.
        if (!isSecondaryBucket) {
          setProviderListLoading(false)
        }
      }

      // This request will not emit again. A pending buffer reveal already
      // consumed the trigger; clear anything left so a later load is untagged.
      providersShownTriggerRef.current = null
    } finally {
      if (previousSelectedSlot?.slot && focusProviderCardRef?.current) {
        scrollContainer?.scrollTo({
          behavior: 'smooth',
          top: focusProviderCardRef.current.offsetTop - 380,
        })
        sessionStorage.setItem(sessionStorageKeys.PREVIOUS_SELECTED_SLOT, null)
      }

      const omitQueryArray = ['offset', 'partnershipCode']
      const parsedLatestQuery = _omit(parsedQuery, omitQueryArray)

      previousQueryRef.current = qs.stringify(parsedLatestQuery, {
        skipNulls: true,
      })
    }
  }

  // make an API call to get additional providers
  const getMoreProviders = () => {
    const {
      slotStartDate = new Date().getTime().toString(),
      latitude,
      longitude,
      state,
      gender,
      ...restQuery
    } = queryRef.current

    if (!!state && !!latitude && !!longitude) {
      const genderValues = getGenderValuesFromIds(
        gender as string[],
        formattedGroupedFilters[ProviderFilterTypes.GENDER]
      )

      // Note: offset and limit are handled internally in fetchProviders
      // Not passing them here since they should not come from URL
      fetchProviders({
        slotStartDate,
        latitude,
        longitude,
        state,
        ...(genderValues && { gender: genderValues }),
        ...restQuery,
      })
    }
  }

  /**
   * Attempts the Day of Week empty-state fallback exactly once per waterfall
   * session. Removes DOW from queryRef, records a human-readable label in
   * removedFilters, signals SearchPage to switch the date bar to a normal
   * 5-day range, then fires the next provider fetch.
   *
   * Returns `true` if the attempt was started (callers should return immediately
   * since a fetch is already in flight). Returns `false` if DOW was not set or
   * the fallback has already run.
   */
  const attemptDayOfWeekFallback = (): boolean => {
    const selectedDayOfWeek = getSelectedDayOfWeek(queryRef.current)

    if (!selectedDayOfWeek || dayOfWeekFallbackAttempted.current) {
      return false
    }

    dayOfWeekFallbackAttempted.current = true

    const dowLabel = summarizeProfileDayOfWeekSelection(selectedDayOfWeek)
    removedFilters.current = [...removedFilters.current, dowLabel]
    queryRef.current = { ...queryRef.current, dayOfWeek: undefined }

    // Tell SearchPage so ProvidersList can switch to a normal date range.
    onSetDowFallbackActive?.(true)

    getMoreProviders()
    return true
  }

  // Load more providers
  // Filters where multiple selected values should be treated as a single "OR" condition.
  // When removing in the waterfall we should drop all the values together instead of one-by-one.

  const loadMoreProviders = (): boolean => {
    let didFindFilterToRemove = false

    // Guard on there being no providers on screen — neither direct matches
    // nor waterfall buckets — so the zero-provider empty state can never show
    // while cards are rendered (the stretched-card + duplicate empty-state bug).
    const hasProvidersOnScreen =
      !!providerList?.length ||
      waterfallResult.some((bucket) => bucket.results.length > 0)

    for (
      let i = filterIndex.current;
      i < droppableSecondaryFilters.length;
      i++
    ) {
      const filter = droppableSecondaryFilters[i]
      const secondaryFilterValue = queryRef.current[filter]
      const isOrMultiselectFilter = SECONDARY_OR_MULTISELECT_FILTERS.includes(
        filter
      )

      // hide show more button when all the secondary filters are removed
      let hideShowMoreButtonLimit = droppableSecondaryFilters.length - 1

      // For regular multi-select filters we need to account for extra values that
      // will be removed one-by-one. OR filters are treated as a single unit and
      // therefore shouldn't extend the limit here.
      if (
        Array.isArray(secondaryFilterValue) &&
        secondaryFilterValue.length > 1 &&
        !isOrMultiselectFilter
      ) {
        hideShowMoreButtonLimit =
          hideShowMoreButtonLimit + secondaryFilterValue.length - 1
      }

      if (i === hideShowMoreButtonLimit) {
        setShowLoadMoreButton(false)
      }

      // this case handles states with no providers.
      if (
        !hasSecondaryFilters(queryRef.current, droppableSecondaryFilters) &&
        filterIndex.current === 0 &&
        !hasProvidersOnScreen
      ) {
        // Before giving up, try a one-shot DOW-drop if the query has a Day of
        // Week filter. If the attempt starts, skip the rest of the loop so we
        // don't prematurely flag zero-provider state while the fetch is
        // in-flight.
        if (attemptDayOfWeekFallback()) {
          return true
        }

        onSetIsZeroProviderState(true)
      }

      if (secondaryFilterValue) {
        if (
          Array.isArray(secondaryFilterValue) &&
          secondaryFilterValue.length > 0 &&
          !isOrMultiselectFilter
        ) {
          // Remove the last element from the array
          const updatedFilterValue = [...secondaryFilterValue]
          const removedValue = updatedFilterValue.pop()

          const removedFilterValue = getFiltersValues(
            { ...queryRef.current, [filter]: removedValue },
            [filter]
          )

          if (updatedFilterValue.length === 0) {
            queryRef.current = { ...queryRef.current, [filter]: undefined }
          } else {
            queryRef.current = {
              ...queryRef.current,
              [filter]: updatedFilterValue,
            }
          }

          removedFilters.current = [
            ...removedFilters.current,
            ...removedFilterValue,
          ]
        } else {
          const removedFilterValue = getFiltersValues(queryRef.current, [
            filter,
          ])
          queryRef.current = { ...queryRef.current, [filter]: undefined }
          removedFilters.current = [
            ...removedFilters.current,
            ...removedFilterValue,
          ]
        }

        // check if there are any more secondary filters on the queryRef
        if (!hasSecondaryFilters(queryRef.current, droppableSecondaryFilters)) {
          setShowLoadMoreButton(false)
        }

        // Every caller (click, empty-page retry, auto-advance) starts here.
        // Hold the Find more button open so its spinner shows until this
        // request paints, instead of the "no more matches" line.
        setProviderListLoading(true)
        getMoreProviders()
        filterIndex.current = i
        didFindFilterToRemove = true
        break
      }
    }

    // When all secondary filters have been exhausted and no more filters can
    // be removed, try the DOW fallback one last time (covers the case where
    // secondaries existed and were all dropped but results are still zero).
    // If that starts a fetch, bail so the spinner stays alive. Otherwise just
    // stop the loading state — "no more matches" / zero-state is already set.
    if (!didFindFilterToRemove) {
      if (attemptDayOfWeekFallback()) {
        return true
      }

      setProviderListLoading(false)

      // An exhausted waterfall (no secondary filters left to drop, DOW
      // fallback exhausted too) with an empty screen is the zero-provider
      // empty state for every insurance type, not just Medicaid — nothing
      // else will flip it once secondaries run out mid-waterfall.
      if (!hasProvidersOnScreen) {
        onSetIsZeroProviderState(true)
      }

      return false
    }

    return true
  }

  useEffect(() => {
    onSetIsZeroProviderState(false)
    bucketIteration.current = 0
    dayOfWeekFallbackAttempted.current = false
    onSetDowFallbackActive?.(false)
    // Reset bucket pagination state on new search
    setBucketPaginationState({})
    // Invalidate in-flight requests without reusing an id. Resetting to 0 lets
    // the next fetch reuse an id that a previous search's GET_SLOTS may still
    // hold, so that response would pass the freshness guard.
    latestRequestIdRef.current += 1
    providersShownTriggerRef.current = null
    bucketOffsetRef.current = {}
    // A new search gets a fresh cap and an empty buffer
    partialBufferRef.current = {}
    setBufferedCount({})
    remainingBudgetRef.current = initialPartialBudget
    pendingBufferRevealRef.current = []
    pendingBufferBucketIndexRef.current = null
  }, [router.asPath])

  // Cleanup effect to abort pending requests on unmount
  useEffect(() => {
    return () => {
      abortApiRequest()
    }
  }, [])

  // Commented useEffect kept for reference
  // useEffect(() => {
  //   const { latitude, longitude, state } = parsedQuery

  //   if (!!state && !!latitude && !!longitude) {
  //     loadMoreProviders()
  //   }
  // }, [])

  /** The above useEffect was the one initially used. But after adding Insurance as a secondary filter,
   * we were running into a bug where if Insurance was applied in one tab and the same URL was opened in
   * another tab, the Insurance name was sometimes not being shown in the missing or in-network text.
   * This is because the insurance isn't a part of the groupedFilters initially sent from getServerSide props
   * and hence the insurance name isn't present on page load. An insurance API is called to fetch the insurance
   * details using the Insurance ID from the URL. But, if the above useEffect runs before that api is successfull,
   * we were running into stale closure issue and the Insurance name wasn't being available at time required.
   * Refer: https://thriveworks.atlassian.net/browse/THRIV-9945
   */
  useEffect(() => {
    const { latitude, longitude, state } = parsedQuery

    if (
      !!state &&
      !!latitude &&
      !!longitude &&
      ((parsedQuery?.insuranceId &&
        insuranceProvider?.id &&
        parsedQuery.insuranceId === insuranceProvider.id) ||
        (!parsedQuery?.insuranceId && !insuranceProvider?.id))
    ) {
      // If the query has Insurance Id, make sure that the insuranceProvider context is also set
      // before loading providers to prevent stale closure issue.
      loadMoreProviders()
    }
  }, [parsedQuery?.insuranceId, insuranceProvider?.id])

  useEffect(() => {
    countDetailsRef.current = countDetails && {
      ...countDetails,
    }
    parentCountDetailsRef.current = countDetails && { ...countDetails }
  }, [countDetails?.slot_sum])

  useEffect(() => {
    validateNoInNetworkProviders()

    return () => {
      setNoWaterfallProviderAcceptsInsurance({
        noWaterfallProviderAcceptsInsurance: false,
        notInNWProvidersCount: 0,
      })
    }
  }, [waterfallResult, insuranceProvider])

  useEffect(() => {
    const allWaterfallProviders = waterfallResult.reduce<Provider[]>(
      (acc, bucket) => {
        const providers = bucket.results.map((result) => result.providerDetails)
        return [...acc, ...providers]
      },
      []
    )

    onSetHasWaterfallProviders?.(allWaterfallProviders.length > 0)

    if (!onSetWaterfallPractices) {
      return
    }

    const practices = formatPracticesFromProviders(allWaterfallProviders)
    onSetWaterfallPractices(practices)
  }, [waterfallResult, onSetWaterfallPractices, onSetHasWaterfallProviders])

  const validateNoInNetworkProviders = () => {
    const isInsuranceAppliedAndNoProviderAcceptsIt =
      // the banner offers self-pay, which Medicaid clients must never be shown
      !isMedicaidSelected &&
      insuranceProvider?.id &&
      waterfallResult.length > 0 &&
      !waterfallResult.some(
        ({ filters, results }) =>
          filters.appliedFilters.includes(insuranceProvider.name) &&
          results.length > 0
      )

    if (isInsuranceAppliedAndNoProviderAcceptsIt) {
      setNoWaterfallProviderAcceptsInsurance({
        noWaterfallProviderAcceptsInsurance: true,
        notInNWProvidersCount: waterfallResult.reduce(
          (acc, { results }) => acc + results.length,
          0
        ),
      })
    } else if (noWaterfallProviderAcceptsInsurance) {
      setNoWaterfallProviderAcceptsInsurance({
        noWaterfallProviderAcceptsInsurance: false,
        notInNWProvidersCount: 0,
      })
    }
  }

  /**
   * Opens a fresh reveal: the variant may put up to one more step's worth of
   * providers on screen before it stops again.
   */
  const startReveal = () => {
    if (!isWaterfallSimplification) {
      return
    }

    remainingBudgetRef.current = WATERFALL_VARIANT_REVEAL_STEP
  }

  /**
   * Emits AVAILABLE_PROVIDERS_SHOWN for cards that just became visible from the
   * buffer (no API round trip). Mirrors the fetchProviders analytics payload so
   * match-type counts stay accurate on buffer-only reveals.
   */
  const emitAvailableProvidersShownForBufferReveal = (
    newlyRevealed: ProviderCardDetails[]
  ) => {
    const existingWaterfallProviders = waterfallResult.flatMap((bucket) =>
      bucket.results.map((result) => result.providerDetails)
    )
    const newlyVisibleProviders = newlyRevealed.map(
      (card) => card.providerDetails
    )
    const totalProviders = [
      ...providerList,
      ...existingWaterfallProviders,
      ...newlyVisibleProviders,
    ]

    const parentCountDetailsProviderId = parentCountDetailsRef.current.provider_count.map(
      (provider) => Object.keys(provider)[0]
    )

    const indirectSlotsCountDetails = {
      slot_sum:
        countDetailsRef.current.slot_sum -
        parentCountDetailsRef.current.slot_sum,
      provider_count: countDetailsRef.current.provider_count.filter(
        (provider) =>
          !parentCountDetailsProviderId.includes(Object.keys(provider)[0])
      ),
      next_available_slot_sum:
        countDetailsRef.current.next_available_slot_sum -
        parentCountDetailsRef.current.next_available_slot_sum,
    }

    // Copy rather than mutate refs — the fetch path mutates slot_sum for mobile
    // event payloads; keep buffer reveals side-effect free on the stored counts.
    const countDetailsForEvent = isDesktop
      ? countDetailsRef.current
      : {
          ...countDetailsRef.current,
          slot_sum: countDetailsRef.current.next_available_slot_sum,
        }
    const directSlotsForEvent = isDesktop
      ? parentCountDetailsRef.current
      : {
          ...parentCountDetailsRef.current,
          slot_sum: parentCountDetailsRef.current.next_available_slot_sum,
        }
    const indirectSlotsForEvent = isDesktop
      ? indirectSlotsCountDetails
      : {
          ...indirectSlotsCountDetails,
          slot_sum: indirectSlotsCountDetails.next_available_slot_sum,
        }

    const shownTrigger = consumeProvidersShownTrigger()

    callRudderEventForAvailableProviderShown({
      providers: totalProviders,
      countDetails: countDetailsForEvent,
      totalProvidersAvailable:
        relaxedTotalAvailableRef.current ?? totalProviders.length,
      groupedFilters: formattedGroupedFilters,
      query,
      startSlotDate,
      selectedPractice,
      user,
      // No fresh providers API response on this path; omit supply-reserve props.
      intakeSupplyReserveParams: {},
      directProvidersCount: providerList.length,
      indirectProvidersCount: totalProviders.length - providerList.length,
      directSlotsCountDetails: directSlotsForEvent,
      indirectSlotsCountDetails: indirectSlotsForEvent,
      timeZone,
      relaxedFilters: [...removedFilters.current],
      ...shownTrigger,
    })
  }

  /**
   * Moves up to one reveal step out of the bucket's buffer and onto the screen.
   * Returns false when the buffer had nothing to give, so the caller falls back
   * to the API.
   *
   * Always folds revealed cards into countDetailsRef (they were excluded when
   * originally fetched). Paints immediately only when the buffer fills the
   * step. A short buffer stays pending so fetchProviders can put it on screen
   * with the rest of the step in one update, and the header count moves once.
   */
  const revealFromBuffer = (bucketIndex: number): boolean => {
    const buffer = partialBufferRef.current[bucketIndex] ?? []

    if (!buffer.length) {
      return false
    }

    const { rendered, buffered } = splitByBudget(
      buffer,
      remainingBudgetRef.current
    )

    if (!rendered.length) {
      return false
    }

    remainingBudgetRef.current = Math.max(
      0,
      (remainingBudgetRef.current ?? 0) - rendered.length
    )

    const revealStepComplete = (remainingBudgetRef.current ?? 0) === 0

    if (revealStepComplete) {
      setWaterfallResult((prev) => {
        const updated = [...prev]

        if (!updated[bucketIndex]) {
          return prev
        }

        updated[bucketIndex] = {
          ...updated[bucketIndex],
          results: [...updated[bucketIndex].results, ...rendered],
        }

        return updated
      })
    }

    setBucketBuffer(bucketIndex, buffered)

    const recentCountDetails = buildCountDetails(rendered)
    countDetailsRef.current = {
      ...countDetailsRef.current,
      slot_sum: countDetailsRef.current.slot_sum + recentCountDetails.slot_sum,
      provider_count: [
        ...countDetailsRef.current.provider_count,
        ...recentCountDetails.provider_count,
      ],
      next_available_slot_sum:
        countDetailsRef.current.next_available_slot_sum +
        recentCountDetails.next_available_slot_sum,
    }

    if (revealStepComplete) {
      pendingBufferRevealRef.current = []
      pendingBufferBucketIndexRef.current = null
      emitAvailableProvidersShownForBufferReveal(rendered)
    } else {
      pendingBufferRevealRef.current = rendered
      pendingBufferBucketIndexRef.current = bucketIndex
    }

    return true
  }

  const emitPendingBufferRevealIfNeeded = () => {
    const pending = pendingBufferRevealRef.current
    const bucketIndex = pendingBufferBucketIndexRef.current

    if (!pending.length) {
      return
    }

    pendingBufferRevealRef.current = []
    pendingBufferBucketIndexRef.current = null

    if (bucketIndex !== null) {
      setWaterfallResult((prev) => {
        const updated = [...prev]

        if (!updated[bucketIndex]) {
          return prev
        }

        updated[bucketIndex] = {
          ...updated[bucketIndex],
          results: [...updated[bucketIndex].results, ...pending],
        }

        return updated
      })
    }

    emitAvailableProvidersShownForBufferReveal(pending)
  }

  const onClickLoadMoreProviders = () => {
    logEvent(RudderEvent.SHOW_PARTIAL_MATCHES_CLICKED)
    providersShownTriggerRef.current = 'find_more'
    startReveal()
    // Show the spinner in this button before the next bucket request starts.
    // loadMoreProviders can hide the idle label in the same turn when the last
    // secondary filter is dropped; loading keeps the button mounted until then.
    // One call only: a second pass finds no filter left and turns loading off.
    setProviderListLoading(true)

    const startedFetch = loadMoreProviders()

    if (!startedFetch) {
      providersShownTriggerRef.current = null
    }
  }

  const missingDetailsInfo: MissingDetailsInfo = useMemo(() => {
    if (!hasSecondaryFilters(queryRef.current, droppableSecondaryFilters)) {
      // initial value set as 100% providers count
      let renderedProvidersCount = providerList.length

      // get the pagination details from the last bucket
      const { providerPagination } = waterfallResult[
        waterfallResult.length - 1
      ] ?? {
        providerPagination: { count: 0 },
      }

      // count the list of rendered providers
      waterfallResult.forEach(({ results: providerCardDetails }) => {
        renderedProvidersCount += providerCardDetails.length
      })

      // Buffered providers were fetched and are one click away, so they are not
      // "missing" even though they have not rendered yet.
      const bufferedProvidersCount = Object.values(bufferedCount).reduce(
        (acc, count) => acc + count,
        0
      )

      const missingCount =
        providerPagination.count -
        renderedProvidersCount -
        bufferedProvidersCount

      if (missingCount > 0) {
        return {
          isMissing: true,
          missingCount: missingCount,
        }
      }
    }

    return {
      isMissing: false,
      missingCount: 0,
    }
  }, [waterfallResult, droppableSecondaryFilters])

  // New function to handle "Show more providers" button click
  const onClickShowMoreProviders = (bucketIndex: number) => {
    const bucket = waterfallResult[bucketIndex]

    if (!bucket) {
      return
    }

    providersShownTriggerRef.current = 'show_more'
    startReveal()

    // Drain the buffer first. Skip the API only when this reveal step is already
    // full (budget hit 0). A short buffer (1–3 cards) must still fetch so the
    // click can fill WATERFALL_VARIANT_REVEAL_STEP when more providers exist.
    const revealedFromBuffer = revealFromBuffer(bucketIndex)

    if (
      isWaterfallSimplification &&
      revealedFromBuffer &&
      (remainingBudgetRef.current ?? 0) === 0
    ) {
      return
    }

    fetchProviders(bucket.bucketQuery, bucketIndex, true)
  }

  const renderInNetworkSection = () => {
    return (
      <div className="~flex">
        <img
          src="/icons/shield-check2.svg"
          srcSet="/icons/shield-check2.webp"
          alt="insurance in-network"
          className="~m-[6px] ~h-5 ~w-5"
        />
        <p className="~font-medium ~font-gibson ~text-sm ~p-[5px]">
          Accepts {insuranceProvider?.name}
        </p>
      </div>
    )
  }

  const renderOutOfNetworkSection = () => {
    return (
      <div className="~flex">
        <img
          src="/icons/shield-cross.svg"
          alt="insurance out-of-network"
          className="~m-[6px] ~h-5 ~w-5"
        />
        <p className="~text-sm ~p-[5px] ~font-gibson">
          <span className="~font-medium">
            Does not accept {insuranceProvider?.name}
          </span>
          , accepts self-pay
        </p>
      </div>
    )
  }

  const displayRemovedFiltersPerBucket = useMemo(
    () => waterfallResult?.map(({ filters }) => filters.removedFilters) ?? [],
    [waterfallResult]
  )

  // The value prop source footnote attaches under whichever of these bottom
  // controls renders last. Find-more and no-more are mutually exclusive and
  // always come after the buckets; when neither shows, the last bucket's
  // "Show more" is the last control.
  const lastBucketHasMore = bucketHasMore((waterfallResult?.length ?? 0) - 1)
  // In the variant the per-bucket "Show more providers" is the next control
  // whenever that bucket can still grow (including a 6-9 prefetch sitting only
  // in the buffer). After the bucket is exhausted, "Find more" stays up while
  // its request is in flight so the label can swap to the spinner. Hiding it
  // on showLoadMoreButton alone removes the button on click, because that flag
  // flips false as soon as the last secondary filter is dropped.
  const showFindMoreButton = isWaterfallSimplification
    ? (showLoadMoreButton || providerListLoading) && !lastBucketHasMore
    : providerListLoading || showLoadMoreButton
  const showNoMoreMessage =
    !hasSecondaryFilters(queryRef.current, droppableSecondaryFilters) &&
    !providerListLoading &&
    !showLoadMoreButton &&
    !missingDetailsInfo.isMissing
  // Only show the source footnote when at least one value prop card renders.
  // Cards live in the direct-match list (providerList) and need 2+ providers, so
  // with 0-1 direct matches there are none and the source would be orphaned.
  const showSource = showValueProp && (providerList?.length ?? 0) > 1
  // Fallback for the rare state where none of the three bottom controls render
  // (e.g. missing-details with the last bucket already exhausted) — keeps the
  // source at the very end so it never disappears while the flag is on.
  const showSourceFallback =
    showSource &&
    !showFindMoreButton &&
    !showNoMoreMessage &&
    !lastBucketHasMore

  return (
    <>
      {/* Scrollable section */}
      {waterfallResult?.map(
        ({ filters, results: providerCardDetails }, idx) => {
          const displayRemovedFilters = displayRemovedFiltersPerBucket[idx]
          const hasBucketCards = providerCardDetails.length > 0
          // Source attaches under this bucket's "Show more" only when it's the
          // last bucket, that bucket actually has a "Show more" button, and
          // neither global control (find-more / no-more) renders. The hasMore
          // check keeps this mutually exclusive with showSourceFallback (which
          // fires when the last bucket is exhausted) so the source never doubles.
          const showSourceUnderBucket =
            showSource &&
            idx === waterfallResult.length - 1 &&
            bucketHasMore(idx) &&
            !showFindMoreButton &&
            !showNoMoreMessage

          return (
            <div
              key={`${
                providerCardDetails?.[0]?.providerDetails.id ?? `bucket-${idx}`
              }-container`}
            >
              <If condition={!isMobileScreen && hasBucketCards}>
                <section
                  className={classNames(
                    styles.normalText,
                    styles.sectionContainer
                  )}
                >
                  <p>
                    <Choose>
                      <When condition={filters.appliedFilters.length === 0}>
                        <span className={styles.boldText}>
                          {`${providerCardDetails.length} available provider${
                            providerCardDetails.length > 1 ? 's' : ''
                          }`}
                        </span>
                        {' in your state.'}
                      </When>
                      <Otherwise>
                        <span className={styles.boldText}>
                          {`${providerCardDetails.length} available provider${
                            providerCardDetails.length > 1 ? 's' : ''
                          }`}
                        </span>
                        {` match${providerCardDetails.length > 1 ? '' : 'es'} ${
                          filters.appliedFilters.length <= 1 ? 'one' : 'some'
                        } of your filters: `}
                        <span>{filters.appliedFilters.join(' \u25cf ')}</span>
                      </Otherwise>
                    </Choose>
                  </p>
                  <If condition={displayRemovedFilters.length > 0}>
                    <p>
                      {' '}
                      <span className={styles.boldText}>Not filtered by: </span>
                      <span className={styles.strikethroughText}>
                        {displayRemovedFilters.join(' \u25cf ')}
                      </span>
                    </p>
                  </If>
                </section>
              </If>

              <If condition={isMobileScreen && hasBucketCards}>
                <section
                  className={classNames(
                    styles.normalText__mobile,
                    styles.sectionContainer__mobile
                  )}
                >
                  <p>
                    <Choose>
                      <When condition={filters.appliedFilters.length === 0}>
                        <span className={styles.boldText__mobile}>
                          {`${providerCardDetails.length} available provider${
                            providerCardDetails.length > 1 ? 's' : ''
                          }`}
                        </span>
                        {' in your state.'}
                      </When>
                      <Otherwise>
                        <span className={styles.boldText__mobile}>
                          {`${providerCardDetails.length} available provider${
                            providerCardDetails.length > 1 ? 's' : ''
                          }`}
                        </span>
                        {` match${providerCardDetails.length > 1 ? '' : 'es'} ${
                          filters.appliedFilters.length <= 1 ? 'one' : 'some'
                        } of your filters: `}
                        <span>{filters.appliedFilters.join(' \u25cf ')}</span>
                      </Otherwise>
                    </Choose>
                  </p>
                  <If condition={displayRemovedFilters.length > 0}>
                    <p>
                      <span className={styles.boldText__mobile}>
                        Not filtered by:{' '}
                      </span>
                      <span className={styles.strikethroughText__mobile}>
                        {displayRemovedFilters.join(' \u25cf ')}
                      </span>
                    </p>
                  </If>
                </section>
              </If>
              {providerCardDetails?.map((providerCardDetails) => {
                const { providerDetails, generatedSlots } = providerCardDetails

                // Calculate bonus time-of-day matches for waterfall buckets
                const removedTimeOfDayValues = getRemovedTimeOfDayValues(
                  filters.removedFilters
                )
                // Use 5-day slot data for bonus badge checks
                const matchedBonusTimeOfDay =
                  removedTimeOfDayValues.length > 0 && generatedSlots
                    ? providerHasTimeOfDaySlots(
                        generatedSlots,
                        removedTimeOfDayValues,
                        timeZone
                      )
                    : []

                return (
                  <div
                    ref={
                      providerDetails.id === previousSelectedSlot?.provider?.id
                        ? focusProviderCardRef
                        : null
                    }
                    key={`${providerDetails.id}-list-card`}
                    className={styles.ProviderCard_container}
                  >
                    <ListCard
                      groupedFilters={formattedGroupedFilters}
                      showDistance={
                        inPersonFilterSelected || isSupportOrThirdPartyUser
                      }
                      slotLocation={SLOT_LOCATION_MAP.indirect_match}
                      providerCardDetails={providerCardDetails}
                      renderCustomInsuranceSection={
                        filters.removedFilters.includes(insuranceProvider?.name)
                          ? renderOutOfNetworkSection
                          : filters.appliedFilters.includes(
                              insuranceProvider?.name
                            )
                          ? renderInNetworkSection
                          : undefined
                      }
                      matchedBonusTimeOfDay={matchedBonusTimeOfDay}
                    />
                  </div>
                )
              })}

              {/* Show more providers button for each bucket */}
              <If condition={bucketHasMore(idx)}>
                <div
                  className={classNames(styles.BucketPaginationContainer, {
                    [styles.withSource]: showSourceUnderBucket,
                  })}
                >
                  <button
                    type="button"
                    disabled={bucketPaginationState[idx]?.isLoading}
                    onClick={() => onClickShowMoreProviders(idx)}
                    className={styles.clearFiltersButton}
                  >
                    <Choose>
                      <When condition={bucketPaginationState[idx]?.isLoading}>
                        <Spinner animation="border" />
                      </When>
                      <Otherwise>Show more providers</Otherwise>
                    </Choose>
                  </button>
                </div>
              </If>
              {/* Source is a sibling AFTER the control (not inside it) so we
                  never touch the control's flex layout. */}
              <If condition={showSourceUnderBucket}>
                <div className={styles.valuePropSourceWrapper}>
                  <SearchValuePropSource />
                </div>
              </If>

              <If
                condition={
                  idx < waterfallResult.length - 1 && !!waterfallResult
                }
              >
                <div
                  className={classNames(
                    commonStyles.waterfall_bucket_divider_container,
                    {
                      [commonStyles.waterfall_bucket_divider_container__mobile]: isMobileScreen,
                    }
                  )}
                >
                  <img
                    src={
                      isMobileScreen
                        ? '/images/squiggle.svg'
                        : '/images/waterfall-bucket-divider.svg'
                    }
                    alt="Waterfall bucket divider"
                    className={commonStyles.waterfall_bucket_divider}
                  />
                </div>
              </If>
            </div>
          )
        }
      )}

      <If condition={showFindMoreButton}>
        <div
          className={classNames(styles.LoadMoreButtonContainer, {
            [styles.withSource]: showSource,
          })}
        >
          <img
            src="/images/squiggle.svg"
            alt="Load more button left squiggle"
            className={styles.button_squiggle}
          />
          <Button
            disabled={providerListLoading}
            onClick={onClickLoadMoreProviders}
            variant="secondary"
            theme="v2"
            size="normal"
            className={styles.load_more_btn}
          >
            <Choose>
              <When condition={providerListLoading}>
                <Spinner />
              </When>
              <Otherwise>Find more partial matches</Otherwise>
            </Choose>
          </Button>
          <img
            src="/images/squiggle.svg"
            alt="Load more button right squiggle"
            className={styles.button_squiggle}
          />
        </div>
        {/* Source is a sibling AFTER the control — the last control whenever it renders. */}
        <If condition={showSource}>
          <div className={styles.valuePropSourceWrapper}>
            <SearchValuePropSource />
          </div>
        </If>
      </If>

      <If condition={showNoMoreMessage}>
        <div
          className={classNames(styles.NoMoreProvidersContainer, {
            [styles.withSource]: showSource,
          })}
        >
          <p>
            There are no more available matches. Check back soon or adjust your
            filters further.
          </p>
        </div>
        {/* Source is a sibling AFTER the control — the last control when it renders. */}
        <If condition={showSource}>
          <div className={styles.valuePropSourceWrapper}>
            <SearchValuePropSource />
          </div>
        </If>
      </If>

      {/* Fallback: no bottom control rendered, so anchor the source at the very
          end of the listings instead of dropping it. */}
      <If condition={showSourceFallback}>
        <div className={styles.valuePropSourceWrapper}>
          <SearchValuePropSource />
        </div>
      </If>
    </>
  )
}

export default ProvidersListWaterfall
